import { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { Sidebar } from './Sidebar';
import { GlobalSearch } from './GlobalSearch';
import { getJobs } from '../../api/jobs';
import { useSocket } from '../../context/SocketContext';
import { useAuth } from '../../context/AuthContext';
import { usePushSubscription } from '../../hooks/usePushSubscription';
import { ChatPanel } from '../chat/ChatPanel';
import { TrialBanner } from './TrialBanner';
import { TrialExpiredModal } from '../billing/TrialExpiredModal';
import { ReconsentModal } from '../legal/ReconsentModal';
import { isOverdueUnassigned, isOverdueAssigned } from '../../utils/jobFilters';

function useOverdueCounts() {
  const [counts, setCounts] = useState({ unassigned: 0, assigned: 0 });
  const { socket } = useSocket();

  const load = async () => {
    try {
      const data = await getJobs({ limit: 200 });
      const jobs = data.jobs;
      const now = new Date();
      let unassigned = 0, assigned = 0;
      jobs.forEach(j => {
        if (isOverdueUnassigned(j, now)) unassigned++;
        else if (isOverdueAssigned(j, now)) assigned++;
      });
      setCounts({ unassigned, assigned });
    } catch { /* sessiz hata */ }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!socket) return;
    socket.on('job:created', load);
    socket.on('job:updated', load);
    socket.on('job:status_changed', load);
    socket.on('job:deleted', load);
    return () => {
      socket.off('job:created', load);
      socket.off('job:updated', load);
      socket.off('job:status_changed', load);
      socket.off('job:deleted', load);
    };
  }, [socket]); // eslint-disable-line react-hooks/exhaustive-deps

  return counts;
}

export function AppLayout() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const overdue = useOverdueCounts();
  const totalOverdue = overdue.unassigned + overdue.assigned;
  const [trialExpiredDetail, setTrialExpiredDetail] = useState(null);
  const [consentOutdatedDetail, setConsentOutdatedDetail] = useState(null);

  usePushSubscription(!!user);

  useEffect(() => {
    const handlePaymentRequired = (e) => setTrialExpiredDetail(e.detail);
    const handleConsentOutdated = (e) => setConsentOutdatedDetail(e.detail);
    window.addEventListener('payment-required', handlePaymentRequired);
    window.addEventListener('consent-outdated', handleConsentOutdated);
    return () => {
      window.removeEventListener('payment-required', handlePaymentRequired);
      window.removeEventListener('consent-outdated', handleConsentOutdated);
    };
  }, []);

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      <Sidebar />
      <div className="flex flex-col flex-1 min-w-0">
        <TrialBanner />
        {/* Top Bar */}
        <header className="h-12 bg-slate-900 border-b border-slate-700 flex items-center px-4 gap-4 shrink-0">
          <GlobalSearch />
          <div className="flex-1" />
          {totalOverdue > 0 && (
            <button
              onClick={() => navigate('/jobs?filter=overdue')}
              className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full cursor-pointer hover:opacity-80 transition
              ${overdue.unassigned > 0 ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
              <AlertTriangle size={13} />
              {totalOverdue} gecikmiş iş
            </button>
          )}
        </header>
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
      <ChatPanel />

      <TrialExpiredModal
        open={!!trialExpiredDetail}
        detail={trialExpiredDetail}
        onClose={() => setTrialExpiredDetail(null)}
      />

      <ReconsentModal
        open={!!consentOutdatedDetail}
        documents={consentOutdatedDetail?.documents}
        onComplete={() => {
          setConsentOutdatedDetail(null);
          window.location.reload();
        }}
      />
    </div>
  );
}
